import {
    configureStore,
    combineReducers,
    Reducer,
    AnyAction,
    ThunkAction,
    Action,
} from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import answersReducer from "./reducers/answers";
import arrayQuestionReducer from "./reducers/arrayQuestion";
import auth, { logout } from "./reducers/auth";
import bankAccountsReducer from "./reducers/bankAccounts";
import client from "./reducers/client";
import bankReducer from "./reducers/countries";
import documentReducer from "./reducers/document";
import featureFlagsReducer from "./reducers/featureFlags";
import navigationReducer from "./reducers/navigation";
import paymentReducer from "./reducers/payment";
import taxProfileReducer from "./reducers/taxProfile";
import taxYearsReducer from "./reducers/taxYears";
import { api } from "../services/connection/api";

// Step 1: Combine all slices
const appReducer = combineReducers({
    [api.reducerPath]: api.reducer,
    auth,
    client,
    answers: answersReducer,
    bank: bankReducer,
    payment: paymentReducer,
    taxProfile: taxProfileReducer,
    arrayQuestion: arrayQuestionReducer,
    navigation: navigationReducer,
    document: documentReducer,
    taxYears: taxYearsReducer,
    featureFlags: featureFlagsReducer,
    bankAccounts: bankAccountsReducer,
});

// Step 2: Typed RootState
export type RootState = ReturnType<typeof appReducer>;

// Step 3: Root reducer that resets on logout
const rootReducer: Reducer<RootState, AnyAction> = (state, action) => {
    if (action.type === logout.fulfilled.type) {
        storage.removeItem("persist:taxzap");
        return appReducer(undefined, action); // resets all slices
    }

    return appReducer(state, action);
};

// Step 4: Persist config
const persistConfig = {
    key: "taxzap",
    storage,
    whitelist: ["auth", "answers", "navigation", "taxProfile", "document", "featureFlags"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Step 5: Configure the store
export const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat(api.middleware),
});

export const persistor = persistStore(store);

// Step 6: Types
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
  >;

export default store;
